import { EventEmitter, Injectable, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { Observable, throwError } from 'rxjs';

import { EditErrorType } from '../../enums';
import { BaseComponent } from '../base/base.component';

@Injectable()
/**
 * This is the abstract edit component that can be used in the edit views.
 * This class is highly recommended to use with Template Driven Forms.
 */
export abstract class EditComponent<T> extends BaseComponent implements OnInit {
  /**
   * Public error.
   *
   * @readonly
   * @type {boolean}
   * @memberof EditComponent
   */
  public get hasError(): boolean {
    return this.error;
  }

  /**
   * Public property that can be used as change detection.
   * @returns {boolean} true if there are no changes.
   */
  public get disabled(): boolean {
    return this.loading || this.noChange;
  }

  /**
   * Private property that can be used as change detection.
   * @returns {boolean} true if there are no changes.
   */
  protected get noChange(): boolean {
    return _.isEqual(this.model, this.originalModel);
  }
  /**
   * The model mostly has id. This can be string or number.
   */
  public id: string | number;

  /**
   * The model, that contains the T object.
   */
  public model: T;

  /**
   * Error type.
   */
  public errorType: EditErrorType;

  /**
   * Options object. This can be added on the sper() call.
   * @example super({preventInit: true});
   */
  protected options: EditComponentOptions = {
    preventInit: false,
    refreshModelFromResponse: true,
  };

  /**
   * This event fires, when the model is updated, and the api responded successfully.
   */
  protected afterModelUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * This event fires, when the model is loaded from the api successfully.
   */
  protected afterModelLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Error flag.
   */
  private error = false;

  /**
   * The original deep copy of the model, that contains the T object. This populates when the request succeeded.
   */
  private originalModel: T;

  /**
   * @param options Options, that runs before the first initialize.
   * @example super({preventInit: true});
   */
  constructor() {
    super();
  }

  /**
   * Private Initialize.
   */
  public ngOnInit() {
    this.startLoading();

    if (!this.options.preventInit) {
      this.getModel().subscribe(
        (res) => this.setModel(res),
        (err) => throwError(this.errorHandler(err, EditErrorType.FailedToLoadModel))
      );
    }
  }

  /**
   * This function starts the uploading methods. UpdateModel(), refreshModel(), etc...
   */
  public update() {
    this.startLoading();

    if (this.beforeUpdate) {
      this.beforeUpdate();
    }

    this.updateModel().subscribe(
      (res) => {
        this.options.refreshModelFromResponse ? this.refreshModel(res) : this.reloadModel();
        this.afterModelUpdated.emit(true);

        if (this.afterUpdate) {
          this.afterUpdate(true);
        }
      },
      (err) => {
        if (this.afterUpdate) {
          this.afterUpdate(false);
        }
        return this.errorHandler(err, EditErrorType.FailedToUpdateModel);
      }
    );
  }

  /**
   * The abstract function, that needs to be implemented in the child element. This method will GET the T model from the api.
   * @returns Observable<T>
   * @example public getModel(): Observable<User> { return this.service.getById(+this.id); }
   */
  protected abstract getModel(): Observable<T>;

  /**
   * The abstract function, that needs to be implemented in the child element. This method will PUT or POST the T model to the api.
   * @returns Observable<T>
   * @example public updateModel(): Observable<User> { return this.service.updateById(this.model, +this.id); }
   */
  protected abstract updateModel(): Observable<T>;

  /**
   * This method repopulates the model and the originalModel. This method uses the loading feature, and loads the model from the backend.
   */
  protected reloadModel() {
    this.startLoading();
    this.getModel().subscribe(
      (res) => this.setModel(res),
      (err) => this.errorHandler(err, EditErrorType.FailedToLoadModel)
    );
  }

  /**
   * This method populates the model and the originalModel.
   * @param resp Succsessful response from the backend.
   */
  private setModel(resp: T) {
    this.model = resp;
    this.originalModel = _.cloneDeep(resp);

    if (this.beforeModelLoaded) {
      this.beforeModelLoaded();
    }

    this.afterModelLoaded.emit(true);

    if (this.afterLoaded) {
      this.afterLoaded(true);
    }

    this.error = false;
    this.stopLoading();
  }

  /**
   * This method populates the model and the originalModel from the update response.
   * @param resp Succsessful response from the backend.
   */
  private refreshModel(response) {
    return this.setModel(response);
  }

  /**
   * Simple error handler.
   * @param {any} error Error
   * @param {EditErrorType} errorType Type of error
   * @param error Type of error.
   */
  private errorHandler(error: any, errorType: EditErrorType): Observable<void> {
    console.error(error);
    this.stopLoading();
    this.error = true;
    this.errorType = errorType;

    return new Observable<void>();
  }
}

export interface EditComponent<T> {
  /**
   * This optional function runs before the private updateModel() function runs.
   */
  beforeUpdate?(): void;

  /**
   * This optional function runs before the afterModelLoaded event emitted.
   */
  beforeModelLoaded(): void;

  /**
   * This optional function runs after the afterModelLoaded event fires runs.
   */
  afterLoaded?(success?: boolean): void;

  /**
   * This optional function runs after the private updateModel() function runs.
   */
  afterUpdate?(success?: boolean): void;
}

export interface EditComponentOptions {
  /**
   * True, if the first getModel() needs to be prevented.
   * @default false
   */
  preventInit?: boolean;

  /**
   * True, if the model needs to be refreshed from the backend response after the update method.
   * @default true
   */
  refreshModelFromResponse?: boolean;
}
